import React, { useEffect } from "react";
import RegistrationRequestsListing from "./RegistrationRequestsListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setPage,
  setRowsPerPage,
  setSearchValue,
  setItems,
  setIsTableLoading,
  setTotalItems,
} from "src/redux/slices/RegistrationRequestsSlice";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import {
  UserModuleActionTypes,
  UserModuleNameTypes,
} from "src/models/userAccess/UserAccess.model";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { useGetAllRegistrationRequestQuery } from "src/services/RegistrationRequestServices";
import {
  useChangeStatusInApproveMutation,
  useChangeSpamStatusMutation,
} from "src/services/RegistrationRequestServices";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import { showToast } from "src/utils/showToaster";
import moment from "moment";
import { RiSpam2Fill } from "react-icons/ri";
import { FcApproval } from "react-icons/fc";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const RegistrationRequestsListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isTableLoading,
    actionBy,
    actionEndDate,
    actionStartDate,
    requestStartDate,
    requestEndDate,
    batch,
    isShowMarkSpam,
    requestStatus,
  } = useSelector((state: RootState) => state.registrationRequest);

  const [changeApprovestatus] = useChangeStatusInApproveMutation();
  const [spamstatus] = useChangeSpamStatusMutation();

  const { data, isLoading, isFetching } = useGetAllRegistrationRequestQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["name", "email", "mobileNumber"],
    page: page,
    filterBy: [
      {
        fieldName: "batchId",
        value: batch?.value ? [batch?.value] : [],
      },
      {
        fieldName: "actionById",
        value: actionBy?.value ? [actionBy?.value] : [],
      },
      {
        fieldName: "isSpam",
        value: isShowMarkSpam ,
      },
      {
        fieldName: "action",
        value: requestStatus?.value ? [requestStatus?.value] : [],
      },
    ],
    dateFilter: {
      dateFilterKey: "createdAt",
      startDate: requestStartDate
        ? moment(requestStartDate).format("YYYY-MM-DD")
        : "",
      endDate: requestEndDate
        ? moment(requestEndDate).format("YYYY-MM-DD")
        : "",
    },
    dateFilterForAction: {
      dateFilterKey: "actionOnDate",
      startDate: actionStartDate
        ? moment(actionStartDate).format("YYYY-MM-DD")
        : "",
      endDate: actionEndDate ? moment(actionEndDate).format("YYYY-MM-DD") : "",
    },
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (requestId: string , row:any) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center  font-semibold">
            <FcApproval className="text-lg" /> Approve
          </div>
        ),
        isHideOptions :  row?.action === "COMPLETED" ? true : false ,
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want to approve this Registration ? ",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                changeApprovestatus(requestId).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.APPROVE ,
        moduleName: UserModuleNameTypes.REGISTRATION_REQUESTS,
      },
      {
        label: (
          <div className="flex gap-2 items-center  font-semibold text-red-700">
            <RiSpam2Fill className="text-lg" /> mark as spam
          </div>
        ),
        isHideOptions: row?.isSpam ? true : false ,
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure want move this request in spam?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result?.isConfirmed) {
                spamstatus(requestId).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },
        actionName: UserModuleActionTypes.MARK_AS_SPAM,
        moduleName: UserModuleNameTypes.REGISTRATION_REQUESTS,
      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: "createdAt",
      headerName: "Request Date",
      extraClasses: "min-w-[100px] py-1 max-w-[150px]",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium text-base">
            {formatedDateTimeIntoIst(row.createdAt, "DD MMM yyyy")}
          </div>
          <div className="text-[12px] font-medium text-slate-400">
            {formatedDateTimeIntoIst(row.createdAt, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Status",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        return (
          <div>
            {row?.isSpam ? (
              <span className="text-red-600 font-medium">Spam</span>
            ) : row?.action === "COMPLETED" ? (
              <span className="text-green-700 font-medium">Approved</span>
            ) : (
              <span className="text-yellow-600 font-medium"> Pending</span>
            )}
          </div>
        );
      },
    },
    { field: "name", headerName: "Name", flex: "flex-[1_1_0%]" },
    {
      field: "mobileNumber",
      headerName: "Mobile number",
      flex: "flex-[1_1_0%]",
      extraClasses: "max-w-[150px]",
    },
    {
      field: "email",
      headerName: "Email",
      flex: "flex-[1_1_0%]",
      extraClasses: "max-w-[180px] min-w-[180px]",
      renderCell: (row: any) => {
        return <div className="text-[13px]">{row?.email}</div>;
      },
    },
    { field: "batchName", headerName: "Batch Name", flex: "flex-[1_1_0%]" },
    {
      field: "emergencyContactNumber",
      headerName: "Emergency Contact",
      flex: "flex-[1_1_0%]",
    },
    {
      field: "whatsappNumber",
      headerName: "Whatsapp Number",
      flex: "flex-[1_1_0%]",
    },
    { field: "actionByName", headerName: "Action By", flex: "flex-[1_1_0%]" },
    {
      field: "actionOnDate",
      headerName: "Action Date",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) =>
        row?.actionOnDate ? (
          <div>
            <div className="text-slate-700 font-medium text-base">
              {formatedDateTimeIntoIst(row.actionOnDate, "DD MMM yyyy")}
            </div>
            <div className="text-[12px] font-medium text-slate-400">
              {formatedDateTimeIntoIst(row.actionOnDate, "hh:mm A")}
            </div>
          </div>
        ) : null,
    },
    {
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      extraClasses: " py-2",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id , row);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];
  const CustomArray = [UserModuleActionTypes.APPROVE , UserModuleActionTypes.MARK_AS_SPAM]
  return (
    <div>
      <RegistrationRequestsListing
        columns={
          isCheckColumnPermission({
          moduleName: UserModuleNameTypes.REGISTRATION_REQUESTS,
          actionName: UserModuleActionTypes.LIST,
          columns: columns,
          actionArray: CustomArray
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
    </div>
  );
};

export default RegistrationRequestsListingWrapper;
